import React from "react";
import _ from "lodash";
import clsx from "clsx";

import { makeStyles, Grid } from "@material-ui/core";

import InputFormElement, {
  CurrencyNumberFormat,
} from "../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import {getNumberValue} from "../../../../../utils";
import {
  PriceEurCol,
  VirtualDiscountInput,
  NoChangeError,
  DiscountTotal,
} from "../../../../TradeStep/components/PortfolioTrade/table-data";

import {
  DeleteActionColumn,
  DiscountColumn,
  NameCol,
  SRRICol,
  SustainabilityCol,
  BrokerFeeColumn,
} from "../../PortfolioTradeV2/table-data";
import SavingPlanSettingsForTrading from "../components/SavingPlanSettingsForTrading";
import {withEuroOrDash} from "../../../../../../../utils/utils";
import {MPExpandCol, MPWeightCol, PriceColumn, PortfolioNameCol} from "../../shared/modelPortfolioTableColumns";

const useCellStyles = makeStyles((theme) => ({
  inputRoot: {
    margin: 0,
    height: 30,
    width: '100%',
    fontSize: 14,
    "&.Mui-error": {
      borderColor: "#f44336",
    },
    "& .MuiInputAdornment-positionEnd": {
      margin: 0,
    },
    "& .MuiButtonBase-root.MuiIconButton-root": {
      padding: 6,
    },
  },
  inputRootChanged: {
    "& .MuiInputBase-input": {
      color: theme.palette.primary.main,
    },
  },
  tooltip: {
    position: "relative",
    top: -10,
  },
  dateTooltip: {
    position: "relative",
    top: 12,
    marginLeft: -5,
  },
  disabledLabel: {
    border: "1px solid #80858C",
    color: "#80858C",
    borderRadius: 2,
    fontFamily: "Roboto-Regular",
    fontSize: 10,
    textTransform: "uppercase",
    padding: "4px 6px",
  },
  actionCell: {
    display: "inline-flex",
    verticalAlign: "top",
    position: "relative",
    zIndex: 0,
    width: "100%",
  },
  weightCell: {
    lineHeight: "40px",
  },
  subNavigationButton: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    padding: 5,

    "&.only-icon": {
      padding: 0,
      marginLeft: 5,
      "&:first-child": { marginLeft: 0 },
      "& span:nth-child(2)": { marginRight: 0 },
    },

    "&.error": {
      "& span:nth-child(2)": { color: "rgb(244, 70, 56)" },
      "& span:first-child": { backgroundColor: "rgba(244, 70, 56, 0.2)" },
    },

    "&:hover": { cursor: "pointer" },
    "&:hover span:first-child": { width: "100%", height: "100%", borderRadius: 5 },

    "& span:first-child": {
      transition: "all",
      transitionDuration: 150,
      height: 28,
      width: 28,
      borderRadius: 20,
      backgroundColor: "#CCE9FA",
      position: "absolute",
    },

    "& span:nth-child(2)": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: 28,
      width: 28,
      borderRadius: 20,
      marginRight: 8,
      position: "relative",
      zIndex: 99,
    },

    "& span:last-child": {
      fontFamily: "Roboto-Bold",
      fontSize: 14,
      color: theme.palette.primary.main,
      position: "relative",
      zIndex: 99,
    },
  },
}));

const PlanDiscountColumn = {
  ...DiscountColumn,
  body: [
    ({ item, options }) => {
      if (item.mpComponent) return null;

      const classes = useCellStyles();

      if (options.isVirtual) {
        return <VirtualDiscountInput item={item} options={options} classes={classes} />;
      }

      const isChanged = item.action === "edit" && item.is_changed && _.get(item, "changes.discount");

      return (
        <DiscountTotal
          item={item}
          options={options}
          disabled={item.action === "delete"}
          onChange={(value) => options.handleDiscountChange && options.handleDiscountChange(item, value)}
          custom_classes={{
            ...classes,
            inputRoot: clsx(classes.inputRoot, isChanged && classes.inputRootChanged),
          }}
        />
      );
    },
  ],
};

const getTotalAmount = (items) => {
  return (items || []).reduce((sum, item) => {
    return sum + getNumberValue(item.transaction_value) || 0
  }, 0);
}

const AmountColumn = {
  header: [({ options }) => (options.isVirtual ? "Bruttobetrag" : <>Betrag<br/>Ausführung</>)],
  body: [
    ({ item, options }) => {
      if (item.mpComponent) return withEuroOrDash(item.allocated_amount);

      const classes = useCellStyles();
      const isChanged = item.action === "edit" && item.is_changed && _.get(item, "changes.transaction_value");

      return (
        <Grid container spacing={1}>
          <Grid item xs={12} className={isChanged ? classes.inputRootChanged : ""}>
            <InputFormElement
              disabled={item.action === "delete"}
              error={item.errors && item.errors.transaction_value}
              value={getNumberValue(item.transaction_value)}
              onChange={(value) => options.handleAmountChange && options.handleAmountChange(item, value)}
              inputComponent={CurrencyNumberFormat}
              inputProps={{ inputProps: { allowNegative: false, fixedDecimalScale: true } }}
              type={"text"}
              custom_classes={classes}
            />
          </Grid>
          <Grid item xs={12}>
            <SavingPlanSettingsForTrading item={item} options={options} classes={classes} />
          </Grid>
        </Grid>
      );
    },
    NoChangeError,
  ],
  footer: [({ items }) => <b>{withEuroOrDash(getTotalAmount(items.filter(i => !i.mpComponent)))}</b>],
  key: "AmountColumn",
  align: "right",
  cellClassName: 'with-input amount-cell',
};

const PlansNameColumn = {
  ...NameCol,
  colSpan: {footer: 2},
}

const PlanSRICol = {
  ...SRRICol,
  footer: [() => 'Gesamt'],
}

const ModelportfolioPlanSRICol = {
  ...SRRICol,
  footer: [() => null],
  footerCellClassName: 'bg-white'
}

export const getPayoutPlansTableStructure = (isModelPortfolio) => {
  if (isModelPortfolio) {
    return [
      MPExpandCol,
      PortfolioNameCol,
      SustainabilityCol,
      ModelportfolioPlanSRICol,
      PriceColumn,
      MPWeightCol,
      AmountColumn,
      DeleteActionColumn,
    ];
  }

  return [
    PlansNameColumn,
    SustainabilityCol,
    PlanSRICol,
    AmountColumn,
    DeleteActionColumn,
  ];
};

export const getSavingsPlansTableStructure = (action, isModelPortfolio, isVirtual) => {
  if (isModelPortfolio) {
    return [
      MPExpandCol,
      PortfolioNameCol,
      SustainabilityCol,
      ModelportfolioPlanSRICol,
      PriceColumn,
      MPWeightCol,
      AmountColumn,
      BrokerFeeColumn,
      PlanDiscountColumn,
      DeleteActionColumn,
    ];
  }

  if (!isVirtual) {
    return [
      PlansNameColumn,
      SustainabilityCol,
      PlanSRICol,
      AmountColumn,
      BrokerFeeColumn,
      PlanDiscountColumn,
      DeleteActionColumn,
    ];
  }

  const structure = [
    PlansNameColumn,
    PriceEurCol,
    AmountColumn,
    BrokerFeeColumn,
    PlanDiscountColumn,
    DeleteActionColumn,
  ];

  if (action === "delete") {
    structure.splice(1, 1); // remove PriceEurCol
  }

  return structure;
};
